import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/api';
import { X, Loader2, CheckCircle2, AlertCircle, Layers, Play, FileSpreadsheet } from 'lucide-react';

const AI_TRADES = [
  'Mechanical', 'Electrical', 'Plumbing', 'Fire Protection',
  'Architectural', 'Structural', 'Roofing', 'Flooring', 'Painting',
  'Glazing', 'Civil', 'Landscape',
];

export default function FloorPlanBatchTakeoff({ projectId, onClose, onDone }) {
  const [drawingSets, setDrawingSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState(null);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get(`/closeout/projects/${projectId}/drawing-sets`)
      .then(r => {
        const sets = r.data || [];
        setDrawingSets(sets);
        if (sets.length > 0) setSelectedSetId(sets[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);

  const toggleTrade = (t) => {
    setSelectedTrades(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
    );
  };

  const pollJob = useCallback(async (jid) => {
    try {
      const res = await api.get(`/closeout/projects/${projectId}/batch-jobs/${jid}`);
      setJobStatus(res.data);
      if (res.data.status === 'done' || res.data.status === 'error') {
        clearInterval(pollRef.current);
        setRunning(false);
      }
    } catch { /* ignore */ }
  }, [projectId]);

  const handleStart = async () => {
    if (!selectedSetId) return;
    setRunning(true);
    setJobStatus(null);
    try {
      const res = await api.post(
        `/closeout/projects/${projectId}/drawing-sets/${selectedSetId}/ai-takeoff-all`,
        { trades: selectedTrades }
      );
      setJobId(res.data.job_id);
      setJobStatus({ status: 'running', total_sheets: res.data.total_sheets, processed_sheets: 0, total_proposed: 0 });
      pollRef.current = setInterval(() => pollJob(res.data.job_id), 3000);
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Failed to start batch takeoff');
      setRunning(false);
    }
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const isDone = jobStatus?.status === 'done';
  const progress = jobStatus ? Math.round((jobStatus.processed_sheets / Math.max(jobStatus.total_sheets, 1)) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose} data-testid="batch-takeoff-modal">
      <div className="bg-card border border-border rounded-md shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-cyan-500" />
            <h3 className="text-sm font-heading text-foreground">Multi-Sheet AI Takeoff</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="batch-takeoff-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : drawingSets.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No drawing sets found. Upload a drawing set first.</p>
          ) : (
            <>
              {/* Drawing set selector */}
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-2 block">Drawing Set</label>
                <select value={selectedSetId || ''} onChange={e => setSelectedSetId(e.target.value)}
                  disabled={running}
                  data-testid="batch-set-select"
                  className="w-full px-3 py-2 text-sm bg-secondary border border-border rounded-sm text-foreground disabled:opacity-50">
                  {drawingSets.map(ds => (
                    <option key={ds.id} value={ds.id}>
                      {ds.name} — {ds.revision_label} ({ds.sheet_count || ds.total_sheets || '?'} sheets)
                    </option>
                  ))}
                </select>
              </div>

              {/* Trade filter */}
              <div>
                <label className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-2 block">
                  Trades Filter <span className="text-muted-foreground/60">(empty = auto-detect per sheet)</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {AI_TRADES.map(t => (
                    <button key={t} onClick={() => toggleTrade(t)} disabled={running}
                      data-testid={`batch-trade-${t.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`px-2.5 py-1 text-[10px] font-heading rounded-sm border transition-all ${
                        selectedTrades.includes(t)
                          ? 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-300 dark:border-cyan-800'
                          : 'text-muted-foreground border-border hover:bg-muted/50'
                      } disabled:opacity-50`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Progress */}
              {jobStatus && (
                <div className="space-y-3" data-testid="batch-progress">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground font-mono">
                      {jobStatus.processed_sheets}/{jobStatus.total_sheets} sheets processed
                    </span>
                    <span className="font-heading text-foreground">{progress}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 transition-all duration-500 rounded-full"
                      style={{ width: `${progress}%` }}
                      data-testid="batch-progress-bar" />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {jobStatus.status === 'running' && <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-500" />}
                    {isDone && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                    {jobStatus.status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                    <span>
                      {isDone
                        ? `Complete — ${jobStatus.total_proposed} measurements proposed`
                        : jobStatus.status === 'error'
                          ? 'Batch job encountered an error'
                          : 'Processing sheets with AI vision...'
                      }
                    </span>
                  </div>

                  {/* Sheet results */}
                  {(jobStatus.sheet_results || []).length > 0 && (
                    <div className="border border-border rounded-sm max-h-48 overflow-y-auto">
                      <table className="w-full text-[10px]">
                        <thead>
                          <tr className="border-b border-border bg-muted/30">
                            <th className="px-2 py-1.5 text-left font-heading text-muted-foreground">Sheet</th>
                            <th className="px-2 py-1.5 text-left font-heading text-muted-foreground">Trade</th>
                            <th className="px-2 py-1.5 text-right font-heading text-muted-foreground">Proposed</th>
                            <th className="px-2 py-1.5 text-center font-heading text-muted-foreground">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobStatus.sheet_results.map((sr, i) => (
                            <tr key={i} className="border-b border-border/30">
                              <td className="px-2 py-1 text-foreground font-mono">{sr.sheet_number || sr.sheet_name || `Sheet ${i+1}`}</td>
                              <td className="px-2 py-1 text-muted-foreground">{sr.trade}</td>
                              <td className="px-2 py-1 text-right font-mono text-foreground">{sr.proposed}</td>
                              <td className="px-2 py-1 text-center">
                                {sr.status === 'done' && <CheckCircle2 className="w-3 h-3 text-emerald-500 inline" />}
                                {sr.status === 'skipped' && <span className="text-muted-foreground">skip</span>}
                                {sr.status === 'error' && <AlertCircle className="w-3 h-3 text-red-500 inline" />}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <p className="text-[10px] text-muted-foreground font-mono">
            All measurements marked as "proposed" for review
          </p>
          <div className="flex items-center gap-2">
            {isDone && (
              <button onClick={onDone} data-testid="batch-takeoff-review-btn"
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-heading uppercase bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 transition-colors">
                <FileSpreadsheet className="w-3.5 h-3.5" /> Review Measurements
              </button>
            )}
            {!running && !isDone && (
              <button onClick={handleStart} disabled={!selectedSetId || running} data-testid="batch-takeoff-start-btn"
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-heading uppercase bg-cyan-600 text-white rounded-sm hover:bg-cyan-700 transition-colors disabled:opacity-50">
                <Play className="w-3.5 h-3.5" /> Start Batch Takeoff
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
